/**
 * Notification Service — maps ZentroBooks AI data into user-facing notifications.
 * All data comes from existing API endpoints. No fake data.
 */

import { listWorkflows, getQueue, analyzeFinancials } from "./aiAgentApi";

export interface Notification {
  id: string;
  type: "warning" | "info" | "success" | "action";
  icon: "alert" | "check" | "chart" | "brain" | "clock";
  title: string;
  message: string;
  timestamp: string;
  actionUrl?: string;
  read: boolean;
}

// Local read state (persisted in localStorage)
function getReadIds(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem("zentro_notif_read") || "[]"));
  } catch { return new Set(); }
}

function saveReadIds(ids: Set<string>) {
  localStorage.setItem("zentro_notif_read", JSON.stringify([...ids]));
}

export function markAsRead(id: string) {
  const ids = getReadIds();
  ids.add(id);
  saveReadIds(ids);
}

export function markAllAsRead(notifications: Notification[]) {
  const ids = getReadIds();
  notifications.forEach(n => ids.add(n.id));
  saveReadIds(ids);
}

/**
 * Fetch all notifications from real ZentroBooks data.
 */
export async function getNotifications(businessId: number): Promise<Notification[]> {
  const notifications: Notification[] = [];
  const readIds = getReadIds();
  const now = new Date();

  try {
    // 1. Workflows → paused/completed notifications
    const workflows = await listWorkflows(businessId);
    for (const wf of (workflows || []).slice(0, 5)) {
      if (wf.status === "paused") {
        notifications.push({
          id: `wf-paused-${wf.id}`,
          type: "action",
          icon: "clock",
          title: "Approval Required",
          message: `"${wf.workflow_type}" workflow paused at step: ${(wf.current_step || "").replace(/_/g, " ")}`,
          timestamp: wf.updated_at || wf.created_at,
          actionUrl: "/ai-bookkeeper",
          read: readIds.has(`wf-paused-${wf.id}`),
        });
      } else if (wf.status === "completed") {
        const age = now.getTime() - new Date(wf.updated_at || wf.created_at).getTime();
        if (age < 24 * 60 * 60 * 1000) { // only show completed in last 24h
          notifications.push({
            id: `wf-done-${wf.id}`,
            type: "success",
            icon: "check",
            title: "Books Ready",
            message: `"${wf.workflow_type}" workflow completed successfully`,
            timestamp: wf.updated_at || wf.created_at,
            actionUrl: "/ai-bookkeeper",
            read: readIds.has(`wf-done-${wf.id}`),
          });
        }
      } else if (wf.status === "failed") {
        notifications.push({
          id: `wf-fail-${wf.id}`,
          type: "warning",
          icon: "alert",
          title: "Workflow Failed",
          message: `"${wf.workflow_type}" failed at: ${(wf.current_step || "").replace(/_/g, " ")}`,
          timestamp: wf.updated_at || wf.created_at,
          actionUrl: "/ai-bookkeeper",
          read: readIds.has(`wf-fail-${wf.id}`),
        });
      }
    }

    // 2. Pending actions → approval notifications
    const pending = await getQueue(businessId, "pending");
    if (pending && pending.length > 0) {
      notifications.push({
        id: `queue-pending-${pending.length}`,
        type: "action",
        icon: "brain",
        title: `${pending.length} Actions Need Approval`,
        message: `AI has ${pending.length} suggested categorization${pending.length > 1 ? "s" : ""} awaiting your review`,
        timestamp: pending[0]?.created_at || now.toISOString(),
        actionUrl: "/ai-bookkeeper",
        read: readIds.has(`queue-pending-${pending.length}`),
      });
    }

    // 3. Insights → warnings
    const year = now.getFullYear();
    const analysis = await analyzeFinancials(`${year}-01-01`, now.toISOString().split("T")[0], businessId);
    if (analysis?.insights) {
      for (const insight of analysis.insights) {
        if (insight.type === "warning" && insight.metric !== null) {
          const id = `insight-${insight.message.substring(0, 20).replace(/\s/g, "-")}-${insight.metric}`;
          notifications.push({
            id,
            type: "warning",
            icon: "chart",
            title: "Financial Alert",
            message: insight.message,
            timestamp: now.toISOString(),
            actionUrl: "/ai-bookkeeper",
            read: readIds.has(id),
          });
        }
      }
    }
  } catch (e) {
    // Notification fetch should never break the app
    console.warn("[Notifications] Fetch error (non-fatal):", e);
  }

  // Sort: unread first, then by timestamp desc
  notifications.sort((a, b) => {
    if (a.read !== b.read) return a.read ? 1 : -1;
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });

  return notifications;
}
